import {useMemo} from 'react'
import type {OptionConfig} from './OptionConfig'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {MetadataSectionInternal} from './sections/MetadataSection'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {ProjectSectionInternal, ReadonlySectionHeader, Section} from '..'
import {noop} from '@github-ui/noop'
import {MilestonesSectionInternal} from './sections/MilestonesSection'
import {LazyRelationsSection} from './sections/RelationsSection'
import {LazyDevelopmentSection} from './sections/development-section/DevelopmentSection'
import {OldExperienceReloadBanner} from './OldExperienceReloadBanner'
import {LazySubscriptionSection} from './sections/LazySubscriptionSection'
import {OptionsSection} from './sections/OptionsSection'
import {ParticipantsSection} from './sections/ParticipantsSection'
import FeedbackLink from './shared/FeedbackLink'
import {graphql, useFragment} from 'react-relay'
import type {IssueSidebarPrimaryQuery$key} from './__generated__/IssueSidebarPrimaryQuery.graphql'

type IssueSidebarProps = {
  sidebarKey: IssueSidebarPrimaryQuery$key
  optionConfig: OptionConfig
  viewer: IssueViewerViewer$data
}

export const IssueSidebarPrimaryGraphqlQuery = graphql`
  fragment IssueSidebarPrimaryQuery on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    id
    ...MetadataSectionFragment
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    ...MilestonesSectionFragment
    ...ParticipantsSectionFragment
    ...OptionsSectionFragment
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IssueSidebarSecondaryGraphqlQuery = graphql`
  fragment IssueSidebarSecondaryQuery on Issue {
    ...DevelopmentSectionFragment
    ...LinkedPullRequests
    ...RelationsSectionFragment
    ...SubscriptionSectionFragment
  }
`

export function IssueSidebar({sidebarKey, optionConfig, viewer}: IssueSidebarProps) {
  const issue = useFragment(IssueSidebarPrimaryGraphqlQuery, sidebarKey)
  const {tasklist_block} = useFeatureFlags()

  const sidebar = useMemo(
    () => (
      <>
        <MetadataSectionInternal
          issue={issue}
          viewer={viewer}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        <PreloadedQueryBoundary
          onRetry={noop}
          fallback={() => {
            return (
              <Section
                sectionHeader={<ReadonlySectionHeader title={'Projects are currently unavailable'} />}
                emptyText={'Please try again later'}
              >
                <></>
              </Section>
            )
          }}
        >
          <ProjectSectionInternal
            selectedProjectId={optionConfig.selectedProjectId}
            allowedProjectOwner={optionConfig.allowedProjectOwner}
            issueOrPullRequest={issue}
            onIssueUpdate={optionConfig.onIssueUpdate}
            singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
            prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
          />
        </PreloadedQueryBoundary>
        <MilestonesSectionInternal
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        {tasklist_block && <LazyRelationsSection issueId={issue.id} onLinkClick={optionConfig.onLinkClick} />}
        <LazyDevelopmentSection
          issueId={issue.id}
          onIssueUpdate={optionConfig.onIssueUpdate}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
        />
        <LazySubscriptionSection issueId={issue.id} />
        <ParticipantsSection issue={issue} />
        <OptionsSection issue={issue} optionConfig={optionConfig} />
        {optionConfig.feedbackLink && <FeedbackLink title="Alpha" feedbackUrl={optionConfig.feedbackLink} />}
        {optionConfig.showReloadInOldExperience && <OldExperienceReloadBanner />}
      </>
    ),
    [issue, optionConfig, tasklist_block, viewer],
  )

  return sidebar
}

try{ IssueSidebar.displayName ||= 'IssueSidebar' } catch {}